"use client";

import { c as _c } from "react/compiler-runtime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { IconButton } from "@mui/material";
import { useRouter } from "next/navigation";
import { pushGtmEvent } from "@/lib/utils/gtmEventHandler";
import MaxWidthWrapper from "@/components/MaxWidthWrapper";

/**
 * GeneralPageHeader
 *
 * A shared, theme-aligned header for general/standalone pages (e.g. legal
 * pages such as Privacy Policy and Terms of Use). Renders a top bar with a
 * smart back button and a title, styled with the project's Material-3
 * typography utilities (m3-*) and design tokens so it matches the in-page
 * headers used elsewhere in the app (e.g. /lead-gen-profiles).
 *
 * Works in both web and webview: in webview there is no global Navbar, so the
 * back button is the primary way to navigate away.
 *
 * @param {string}  title          The page title rendered as the page <h1>.
 * @param {string}  fallbackHref   Where to navigate when there is no browser
 *                                 history to go back to (default "/").
 * @param {object}  gtmEvent       Optional GTM event fired before navigating
 *                                 back, e.g. { event_name, event_action }.
 * @param {boolean} sticky         Whether the header sticks to the top
 *                                 (default true).
 * @param {Function} onBack        Optional override for the back behaviour.
 * @param {string}  className      Additional classes for the outer bar.
 * @param {string}  containerClassName  When set, replaces the default
 *                                 MaxWidthWrapper (max-w-screen-xl) with a
 *                                 plain div using this class, so the bar can
 *                                 line up with a page that uses a different
 *                                 content width (e.g. max-w-[1120px]).
 * @param {boolean} bordered       Whether to draw the bottom border
 *                                 (default true, matches the legal pages).
 * @param {string}  iconColor      Back arrow color (default matches the
 *                                 legal pages' link-blue).
 */
export default function GeneralPageHeader(t0) {
  const $ = _c(20);
  const {
    title,
    fallbackHref: t1,
    gtmEvent,
    sticky: t2,
    onBack,
    className: t3,
    containerClassName: t4,
    bordered: t5,
    iconColor: t6
  } = t0;
  const fallbackHref = t1 === undefined ? "/" : t1;
  const sticky = t2 === undefined ? true : t2;
  const className = t3 === undefined ? "" : t3;
  const containerClassName = t4 === undefined ? "" : t4;
  const bordered = t5 === undefined ? true : t5;
  const iconColor = t6 === undefined ? "var(--colorPrimaryPrimary, #004bee)" : t6;
  const router = useRouter();
  let t7;
  if ($[0] !== fallbackHref || $[1] !== gtmEvent || $[2] !== onBack || $[3] !== router) {
    t7 = () => {
      if (gtmEvent) {
        pushGtmEvent(gtmEvent);
      }
      if (onBack) {
        onBack();
        return;
      }
      if (typeof window !== "undefined" && window.history.length > 1) {
        router.back();
      } else {
        router.push(fallbackHref);
      }
    };
    $[0] = fallbackHref;
    $[1] = gtmEvent;
    $[2] = onBack;
    $[3] = router;
    $[4] = t7;
  } else {
    t7 = $[4];
  }
  const handleBack = t7;
  const Wrapper = containerClassName ? "div" : MaxWidthWrapper;
  const wrapperClassName = containerClassName ? `mx-auto w-full flex items-center gap-2 ${containerClassName}` : "flex items-center gap-2";
  const t8 = `w-full h-[64px] flex items-center z-50 bg-[var(--colorSurfaceSurface,#ffffff)] ${bordered ? "border-b border-[var(--colorOutlineOutline,#aeb7f9)]" : ""} ${sticky ? "sticky top-0" : ""} ${className}`;
  let t9;
  if ($[5] !== iconColor) {
    t9 = <ArrowBackIcon sx={{
      color: iconColor
    }} />;
    $[5] = iconColor;
    $[6] = t9;
  } else {
    t9 = $[6];
  }
  let t10;
  if ($[7] !== handleBack || $[8] !== t9) {
    t10 = <IconButton onClick={handleBack} aria-label="Go back" className="!ml-0">{t9}</IconButton>;
    $[7] = handleBack;
    $[8] = t9;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] !== title) {
    t11 = title && <h1 className="m3-title-large text-[var(--textPrimaryPrimary,#1b1c17)]">{title}</h1>;
    $[10] = title;
    $[11] = t11;
  } else {
    t11 = $[11];
  }
  let t12;
  if ($[12] !== Wrapper || $[13] !== t10 || $[14] !== t11 || $[15] !== wrapperClassName) {
    t12 = <Wrapper className={wrapperClassName}>{t10}{t11}</Wrapper>;
    $[12] = Wrapper;
    $[13] = t10;
    $[14] = t11;
    $[15] = wrapperClassName;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== t12 || $[18] !== t8) {
    t13 = <div className={t8}>{t12}</div>;
    $[17] = t12;
    $[18] = t8;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  return t13;
}